import { useCallback, useEffect, useState } from 'react';
import {
  CONSENT_VERSION,
  type ConsentState,
  denyAll,
  fromChoices,
  grantAll,
  needsPrompt,
  readClient,
  resolveCookieDomain,
  writeClient,
} from '@vib3/consent';
import { captureCurrentTouch } from '../utils/attribution';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

/**
 * Site-wide cookie-consent banner — a floating, square card pinned bottom-left, built from
 * the app's design-system primitives (Card / Button) so it matches colors, typography and
 * dark mode and stays visually consistent with the banners on vib3.tech and app.vib3.tech.
 *
 * Consent state comes from the shared @vib3/consent scheme (`vib3_consent` cookie). On a
 * choice we POST to the canonical hub (app.vib3.tech `/api/consent`), which sets the
 * durable, shared `.vib3.tech` cookie server-side (Safari ITP-safe); if that fails (e.g.
 * localhost), we fall back to a client-side cookie. We then re-run the consent-gated
 * attribution capture so a `marketing` grant takes effect immediately (and a denial drops
 * any stored touch). Dispatch `vib3:open-consent` from anywhere (e.g. a footer link) to
 * re-open it as a preference center.
 */
const COOKIE_NOTICE_URL = 'https://vib3.tech/cookies';
// Hub that owns /api/consent. audit.vib3.tech is on its CORS allowlist; it sets the shared
// `.vib3.tech` cookie so the choice carries across vib3.tech / app.vib3.tech / audit.vib3.tech.
const CONSENT_API = 'https://app.vib3.tech';

function clientCookieOpts() {
  if (typeof window === 'undefined') return {};
  return {
    domain: resolveCookieDomain(window.location.hostname),
    secure: window.location.protocol === 'https:',
  };
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const sync = (force = false) => {
      try {
        const current = readClient();
        if (force || needsPrompt(current, CONSENT_VERSION)) {
          setAnalytics(!!current?.analytics);
          setMarketing(!!current?.marketing);
          setCustomizing(force); // reopening a decided state → show the toggles
          setVisible(true);
        }
      } catch {
        /* malformed cookie → leave hidden, capture stays default-denied */
      }
    };
    sync(false);
    const reopen = () => sync(true);
    window.addEventListener('vib3:open-consent', reopen);
    return () => window.removeEventListener('vib3:open-consent', reopen);
  }, []);

  const persist = useCallback(async (state: ConsentState) => {
    setVisible(false);
    setCustomizing(false);
    try {
      await fetch(`${CONSENT_API}/api/consent`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analytics: state.analytics, marketing: state.marketing }),
      });
    } catch {
      // Hub unreachable (e.g. localhost / CORS) → persist client-side. Never `Secure` over http.
      try {
        writeClient(state, clientCookieOpts());
      } catch {
        /* ignore cookie write failures */
      }
    }
    // Cookie is set now (hub or fallback): re-run the consent-gated capture so a `marketing`
    // grant stores the touch immediately, and a denial clears any stored touch.
    try {
      captureCurrentTouch();
    } catch {
      /* best-effort */
    }
    try {
      window.dispatchEvent(new CustomEvent('vib3:consent-updated'));
    } catch {
      /* ignore */
    }
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed bottom-4 left-4 z-[100] w-[min(23rem,calc(100vw-2rem))] animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <Card className="gap-3 py-4 shadow-2xl">
        <CardHeader>
          <CardTitle>We value your privacy</CardTitle>
          <CardDescription className="mt-1 leading-relaxed">
            We use strictly necessary storage to run the service. With your consent we also use
            analytics and marketing-attribution data (UTM tags and ad click IDs). See our{' '}
            <a
              href={COOKIE_NOTICE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-secondary underline-offset-4 hover:underline"
            >
              Cookie Notice
            </a>{' '}
            and{' '}
            <a href="/privacy" className="font-medium text-secondary underline-offset-4 hover:underline">
              Privacy Policy
            </a>
            .
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-3">
          {customizing && (
            <fieldset className="flex flex-col gap-2.5 rounded-lg border border-border p-3 text-sm">
              <legend className="px-1 text-xs font-medium text-muted-foreground">
                Manage preferences
              </legend>
              <label className="flex items-start gap-2.5 text-muted-foreground">
                <input type="checkbox" checked readOnly aria-readonly className="mt-0.5 size-4 accent-primary" />
                <span>
                  <strong className="font-medium text-foreground">Strictly necessary</strong> — always
                  on (sign-in session, theme, in-progress analysis state).
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-0.5 size-4 accent-primary"
                />
                <span>
                  <strong className="font-medium text-foreground">Analytics</strong> — usage
                  measurement to operate and improve the product.
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-0.5 size-4 accent-primary"
                />
                <span>
                  <strong className="font-medium text-foreground">Marketing</strong> — attribution
                  (UTM, gclid/fbclid) and remarketing.
                </span>
              </label>
            </fieldset>
          )}

          {/* Equal-prominence accept/reject (GDPR: rejecting must be as easy as accepting). */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => persist(denyAll(CONSENT_VERSION))}
            >
              Reject all
            </Button>
            {customizing ? (
              <Button
                size="sm"
                className="flex-1"
                onClick={() => persist(fromChoices({ analytics, marketing }, CONSENT_VERSION))}
              >
                Save choices
              </Button>
            ) : (
              <Button size="sm" className="flex-1" onClick={() => persist(grantAll(CONSENT_VERSION))}>
                Accept all
              </Button>
            )}
          </div>

          {!customizing && (
            <button
              type="button"
              className="self-center text-xs text-muted-foreground underline-offset-4 hover:underline"
              onClick={() => setCustomizing(true)}
            >
              Customize
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
